'use client';

import type { BioContext } from '@ps/functions/bio/bioContext';

import { useBioUserAccess } from '../../../../bio/contexts/user-access/useBioUserAccess';
import { usePostUnlock } from '../../hooks/usePostUnlock';
import type { PostViewModel } from '../../types';
import { PostPaywall } from '../paywall/PostPaywall';
import { MediaCarousel } from './media-carousel/MediaCarousel';
import { PostTextBody } from './text/PostTextBody';

interface Props {
  /** The server-rendered (anonymous, possibly-redacted) view model. */
  post: PostViewModel;
  sellerId: string;
  /** The surface's bio context (`null` on the non-1:1 DM inbox) — drives the
   *  subscribe route on unlock, or the in-place picker when absent. */
  bioContext: BioContext | null;
  /** Show the paywall to the creator too (default off — owner sees content). */
  showForOwner: boolean;
  /**
   * Open the post in place instead of navigating to the permalink (feed
   * context — see {@link PostTextBody}). Forwarded to the unlocked rich-text body.
   */
  onOpen?: () => void;
  /**
   * Detail view (modal / permalink) — the unlocked rich-text body's "Read more…"
   * toggles in place instead of linking out. Forwarded to {@link PostTextBody}.
   */
  detail?: boolean;
}

/**
 * Client layer for a RESTRICTED post: {@link usePostUnlock} re-fetches with the
 * viewer's identity and drives the unlock flows (subscribe / pay / tip). Once the
 * server returns the post un-redacted for an entitled viewer, it renders the real
 * content with no paywall (and a "Post unlocked!" toast fires); otherwise it
 * stacks the {@link PostPaywall} — wired to the unlock actions — over the
 * blurhash-preview media (or a text placeholder). The creator is exempt unless
 * `showForOwner`.
 */
export function GatedPostContent({
  post: initial,
  sellerId,
  bioContext,
  showForOwner,
  onOpen,
  detail,
}: Props) {
  const { currentUserId } = useBioUserAccess();
  const { post, onSubscribe, onPay, onTip } = usePostUnlock(
    initial,
    sellerId,
    bioContext,
  );

  const isOwner = currentUserId != null && currentUserId === sellerId;
  const showPaywall = post.restricted && (showForOwner || !isOwner);

  const paywall = showPaywall ? (
    <PostPaywall
      gating={post.gating}
      onSubscribe={onSubscribe}
      onPay={onPay}
      onTip={onTip}
    />
  ) : undefined;

  if (post.contentKind === 'media') {
    return (
      <MediaCarousel
        medias={post.medias}
        unrestrictedLeadingMediaCount={post.unrestrictedLeadingMediaCount}
        paywall={paywall}
      />
    );
  }

  if (showPaywall) {
    // Rich-text posts have no carousel to sit on — give the lock a placeholder region.
    return <div className="relative min-h-[220px]">{paywall}</div>;
  }

  return (
    <PostTextBody
      bodyHtml={post.bodyHtml}
      permalinkHref={post.permalinkHref}
      onOpen={onOpen}
      detail={detail}
    />
  );
}
